import { useState } from "react";
import { motion } from "framer-motion";
import { Blogs } from "../../data/blogs";
import { Solutions } from "../../data/solutions";

let blogs = Blogs;
let solutions = Solutions;

const headerChildVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: {
        opacity: 1,
        y: 0,
        transition: {
            duration: 0.55,
            ease: [0.22, 1, 0.36, 1],
        },
    },
};

function getCardDirection(index, total) {
    const col = index % 3;
    if (col === 0) return { x: -60, y: 20 };
    if (col === 2) return { x: 60, y: 20 };
    return { x: 0, y: 40 };
}

export default function ScrollableFeedComponent({ type = "solutions" }) {
    const [active, setActive] = useState(null);
    const content = type === "blogs" ? blogs : solutions;

    return (
        <div className={`${type === "blogs"} min-h-screen font-sans`}>

            {/* Header */}
            <motion.div
                className="px-6 pt-20 text-center"
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-80px" }}
                variants={{ hidden: {}, visible: { transition: { staggerChildren: 0.1 } } }}
            >
                <motion.span
                    variants={headerChildVariants}
                    className="inline-block bg-gray-100 text-gray-600 text-xs font-medium px-4 py-1.5 rounded-full mb-6 tracking-wide uppercase"
                >
                    {
                        type === "blogs" ? "Education" : "Platform"
                    }
                </motion.span>

                <motion.h1
                    variants={headerChildVariants}
                    className={`${type === "blogs" ? "text-gray-900" : "text-gray-100"} text-4xl md:text-5xl font-bold mb-4 leading-tight`}
                >
                    {type === "blogs" ? (
                        "Learn the Playbook"
                    ) : (
                        <span>Solutions that empower<br />your business on autopilot.</span>
                    )}
                </motion.h1>

                <motion.p
                    variants={headerChildVariants}
                    className={`${type === "blogs" ? "text-gray-500" : "text-gray-400"} text-lg max-w-xl mx-auto`}
                >
                    {type === "blogs"
                        ? "Real strategies, real use cases, and honest breakdowns of how store credit works in practice. Read up, then put it to work."
                        : "All the tools you need to manage and grow your community, your way. Private, safe, and secure."}
                </motion.p>
            </motion.div>

            {/* Grid */}
            <div
                className="container mx-auto px-6 pb-24 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5"
                style={{ gridTemplateColumns: "repeat(6, minmax(0, 1fr))" }}
            >
                {content.map((f, index) => {
                    const dir = getCardDirection(index, content.length);
                    return (
                        <motion.div
                            key={f.id}
                            initial={{ opacity: 0, x: dir.x, y: dir.y, scale: 0.97 }}
                            whileInView={{ opacity: 1, x: 0, y: 0, scale: 1 }}
                            viewport={{ once: true, margin: "-60px" }}
                            transition={{
                                duration: 0.6,
                                delay: (index % 3) * 0.08,
                                ease: [0.22, 1, 0.36, 1],
                            }}
                            whileHover={{ y: -4, transition: { duration: 0.2, ease: "easeOut" } }}
                            onClick={() => setActive(active === f.id ? null : f.id)}
                            className={`group relative rounded-xl p-6 cursor-default transition-all duration-300 border ${
                                false
                                    ? `${f.bg} border-transparent shadow-lg scale-[1.02]`
                                    : `${f.customTailwind} bg-white border-gray-100 hover:border-gray-200 hover:shadow-md`
                            }`}
                        >
                            <div className={`${f.bg} p-4 rounded-xl`}>
                                <a className="w-full" href={f.src} target="_blank" rel="noreferrer">
                                    {/* Icon */}
                                    <div className="w-14 h-14 mb-4">{f.icon}</div>

                                    {/* Text */}
                                    <div className="flex items-start justify-between mb-1">
                                        <h3 className="font-semibold text-gray-900 text-base">{f.title}</h3>
                                        {Boolean(f.src) && (
                                            <svg
                                                className={`w-4 h-4 mt-0.5 transition-transform duration-300 ${true ? `rotate-45 ${f.accent}` : "text-gray-300"}`}
                                                viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2"
                                            >
                                                <path d="M3 8h10M8 3l5 5-5 5" strokeLinecap="round" strokeLinejoin="round" />
                                            </svg>
                                        )}
                                    </div>
                                    <p className="text-sm text-gray-500 leading-relaxed">{f.description}</p>
                                </a>
                            </div>

                            {/* Mockup */}
                            <div className="mt-5 transition-all duration-300">
                                <div className="rounded-2xl overflow-hidden cursor-default">{f.mockup}</div>
                            </div>
                        </motion.div>
                    );
                })}
            </div>

            {/* CTA */}
            <motion.div
                className="max-w-5xl mx-auto px-6 pb-24 text-center"
                initial={{ opacity: 0, y: 32 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-60px" }}
                transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            >
                <div className="bg-gray-900 rounded-xl p-12">
                    <h2 className="text-3xl font-bold text-white mb-3">Ready to offer store credit?</h2>
                    <p className="text-gray-400 mb-8">Join thousands of brand leaders on Pabloo.</p>
                    <div className="flex gap-3 justify-center flex-wrap">
                        <button
                            className="bg-white text-gray-900 font-semibold text-sm px-6 py-3 rounded-full hover:bg-gray-100 transition-colors"
                            onClick={() => window.location.href = "https://app.pabloo.com/sign-up"}
                        >
                            Get started for free
                        </button>
                    </div>
                </div>
            </motion.div>

        </div>
    );
}